/**
 * QR Code Generator Tool
 */

import QRCode from 'qrcode';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type Ecc = 'L' | 'M' | 'Q' | 'H';

export const QrCodeGeneratorTool: ITransformerTool = {
  id: 'qr-code-generator',
  name: 'QR Code Generator',
  description: 'Generate a QR code from input text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Generators',
  keywords: ['qr', 'qrcode', 'generate', 'barcode', 'image'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.URL],
  outputType: SEMANTIC_TYPES.SVG,

  attributes: [
    {
      name: 'format',
      type: 'select',
      label: 'Output',
      defaultValue: 'svg',
      options: ['svg', 'data-url'],
      description: 'Output SVG markup or a PNG data URL',
    },
    {
      name: 'errorCorrectionLevel',
      type: 'select',
      label: 'Error Correction',
      defaultValue: 'M',
      options: ['L', 'M', 'Q', 'H'],
    },
    {
      name: 'margin',
      type: 'number',
      label: 'Margin',
      defaultValue: 2,
      validation: { min: 0, max: 16, step: 1 },
    },
    {
      name: 'scale',
      type: 'number',
      label: 'Scale',
      defaultValue: 6,
      validation: { min: 1, max: 20, step: 1 },
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) throw ToolError.validation('Input text is required');

    const format = String(config.format ?? 'svg');
    const errorCorrectionLevel = (String(config.errorCorrectionLevel ?? 'M') as Ecc) || 'M';
    const margin = typeof config.margin === 'number' ? (config.margin as number) : 2;
    const scale = typeof config.scale === 'number' ? (config.scale as number) : 6;

    try {
      if (format === 'data-url') {
        const url = await QRCode.toDataURL(text, {
          errorCorrectionLevel,
          margin,
          scale,
        });
        // This is still text, but represents an image data URL.
        return url;
      }

      const svg = await QRCode.toString(text, {
        type: 'svg',
        errorCorrectionLevel,
        margin,
        scale,
      });
      return svg;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to generate QR code';
      throw ToolError.execution(msg);
    }
  },
};
